import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Footprints, Play, Square, Map } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";

const TYPES = ["walk", "hike", "run", "treadmill", "dog_walk"];

export default function Walk() {
  const nav = useNavigate();
  const [running, setRunning] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [steps, setSteps] = useState(0);
  const [activity, setActivity] = useState("walk");
  const [manualSteps, setManualSteps] = useState("");
  const intervalRef = useRef(null);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setSeconds((s) => s + 1);
        setSteps((p) => p + Math.floor(Math.random() * 3) + 1);
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const stop = async () => {
    setRunning(false);
    if (steps > 0) {
      const r = await api.post("/walk", {
        steps, distance_km: (steps / 1300).toFixed(2), duration_min: (seconds / 60).toFixed(1),
        calories: Math.round(steps * 0.04), activity_type: activity,
      });
      toast.success(`Walk logged · +${Math.max(10, Math.floor(steps / 100))} pts`);
      (r.data?.new_badges || []).forEach((b) =>
        toast(`🏅 Badge: ${b.title}`, { description: `${b.milestone_pct}% of annual goal · +${100 + b.milestone_pct * 2} pts` })
      );
      setTimeout(() => nav("/home"), 600);
    }
  };

  const submitManual = async () => {
    const s = Number(manualSteps);
    if (!s || s < 1) return toast.error("Enter step count");
    const r = await api.post("/walk", {
      steps: s, distance_km: +(s / 1300).toFixed(2), duration_min: Math.round(s / 100),
      calories: Math.round(s * 0.04), activity_type: activity,
    });
    toast.success(`+${Math.max(10, Math.floor(s / 100))} pts earned`);
    (r.data?.new_badges || []).forEach((b) =>
      toast(`🏅 Badge: ${b.title}`, { description: `${b.milestone_pct}% of annual goal` })
    );
    setTimeout(() => nav("/home"), 600);
  };

  const min = String(Math.floor(seconds / 60)).padStart(2, "0");
  const sec = String(seconds % 60).padStart(2, "0");

  return (
    <PhoneFrame>
      <div className="px-5 pt-6 pb-10">
        <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="walk-back">
          <ChevronLeft size={16} /> Back
        </button>
        <h1 className="ps-heading text-3xl font-bold mt-4">Active Walk</h1>
        <p className="text-slate-400 text-sm">Track an Active Posture Steps session.</p>

        <div className="mt-6 ps-card p-6 text-center">
          <Footprints size={32} className="mx-auto text-[#2fbf71]" />
          <div className="text-6xl font-bold ps-heading mt-3" data-testid="walk-step-count">{steps}</div>
          <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Steps</div>
          <div className="text-2xl ps-heading mt-3">{min}:{sec}</div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Duration</div>

          <div className="mt-5 grid grid-cols-2 gap-3 text-left">
            <div className="bg-[#142534] rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase">Distance</div>
              <div className="font-bold">{(steps / 1300).toFixed(2)} km</div>
            </div>
            <div className="bg-[#142534] rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase">Calories</div>
              <div className="font-bold">{Math.round(steps * 0.04)}</div>
            </div>
          </div>

          {running ? (
            <button data-testid="walk-stop" onClick={stop} className="mt-5 w-full ps-button-primary inline-flex items-center justify-center gap-2 bg-[#EF4444] hover:bg-[#dc2626]">
              <Square size={18} /> Stop & Save
            </button>
          ) : (
            <button data-testid="walk-start" onClick={() => { setRunning(true); setSeconds(0); setSteps(0); }} className="mt-5 w-full ps-button-primary inline-flex items-center justify-center gap-2">
              <Play size={18} /> Start Walking
            </button>
          )}
        </div>

        <div className="mt-5 ps-card p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Activity Type</div>
          <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
            {TYPES.map((t) => (
              <button
                key={t}
                data-testid={`walk-type-${t}`}
                onClick={() => setActivity(t)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border ${
                  activity === t ? "bg-[#2fbf71] text-[#0a1822] border-transparent" : "border-[#1a2f40] text-slate-300"
                }`}
              >
                {t.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 ps-card p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Manual Entry / Sync</div>
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
            <Map size={14} /> Simulated sync (web demo). Native iOS/Android will auto-sync Apple Health, Google Health Connect, Fitbit.
          </div>
          <div className="flex gap-2">
            <input
              data-testid="walk-manual-input"
              type="number" placeholder="Enter steps"
              value={manualSteps} onChange={(e) => setManualSteps(e.target.value)}
              className="flex-1 bg-[#142534] border border-[#1a2f40] rounded-xl px-4 py-3 text-white outline-none"
            />
            <button data-testid="walk-manual-submit" onClick={submitManual} className="ps-button-primary">Log</button>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
